import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import 'material-symbols/outlined.css';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canAccessAdministration,
  canAccessFullAdministration,
  canAccessCommercial,
  canAccessOnboarding,
  canAccessDashboard,
  canAccessExercises,
  canAccessPerformanceSupportDashboard,
  canAccessRecentBodyPhotosDashboard,
  canAccessSensitiveStudentsDashboard,
  canAccessOrchestrator,
  canAccessPerformance,
  canAccessStudents,
  canAccessTrainings,
} from '../../../utils/permissions';
import {
  buildCanonicalPortalUrl,
  isDeprecatedPortalHostname,
} from '../../../config/hostApps';
import { isHomeDashboardPath } from '../../../pages/Dashboard/homeNavigation';
import {
  COMMERCIAL_LISTING_ITEMS,
  getCommercialListingPath,
  isCommercialPath,
} from '../../../pages/Commercial/commercialNavigation';
import { getOrchestratorTabPath, ORCHESTRATOR_TABS } from '../../../pages/Orchestrator/orchestratorNavigation';
import styles from './Sidebar.module.css';

interface SidebarProps {
  open: boolean;
  isMobile: boolean;
  collapsed: boolean;
  onClose: () => void;
  onToggle: () => void;
  onToggleCollapse: () => void;
}

const Icon: React.FC<{ name: string; className?: string }> = ({ name, className }) => (
  <span className={`material-symbols-outlined ${className || ''}`}>{name}</span>
);

const Sidebar: React.FC<SidebarProps> = ({ open, isMobile, collapsed, onClose, onToggle, onToggleCollapse }) => {
  const { user, signOut, roles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const displayName =
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Usuário';

  const handleManageAccount = () => {
    if (isDeprecatedPortalHostname()) {
      window.location.assign(buildCanonicalPortalUrl('/conta'));
      return;
    }

    navigate('/conta');
    if (isMobile && open) {
      onClose();
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const isCollapsed = isMobile ? false : collapsed;

  const getNavClass = ({ isActive }: { isActive: boolean }) =>
    [
      styles['menu-item'],
      isActive ? styles['menu-item-active'] : '',
      isCollapsed ? styles['menu-item-collapsed'] : '',
      isMobile ? styles['menu-item-mobile'] : '',
    ].filter(Boolean).join(' ');

  const getSubmenuNavClass = ({ isActive }: { isActive: boolean }) => [
    styles['submenu-item'],
    isActive ? styles['submenu-item-active'] : '',
  ].filter(Boolean).join(' ');

  const sidebarClass = [
    styles.sidebar,
    isCollapsed ? styles['sidebar-collapsed'] : '',
    isMobile ? styles['sidebar-mobile'] : '',
    isMobile && open ? styles['sidebar-mobile-open'] : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleMenuClick = () => {
    if (isMobile && open) {
      onClose();
    }
  };

  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    handleMenuClick();

    if (!isDeprecatedPortalHostname()) {
      return;
    }

    event.preventDefault();
    window.location.assign(buildCanonicalPortalUrl(path));
  };

  const isHomeSectionActive = isHomeDashboardPath(location.pathname);
  const isCommercialSectionActive = isCommercialPath(location.pathname);
  const isOrchestratorSectionActive = (
    location.pathname === '/orquestrador'
    || location.pathname.startsWith('/orquestrador/')
  );
  const [isCommercialMenuOpen, setIsCommercialMenuOpen] = React.useState(isCommercialSectionActive);
  const [isOrchestratorMenuOpen, setIsOrchestratorMenuOpen] = React.useState(isOrchestratorSectionActive);

  React.useEffect(() => {
    if (!isCommercialSectionActive) {
      setIsCommercialMenuOpen(false);
    }
  }, [isCommercialSectionActive]);

  React.useEffect(() => {
    if (!isOrchestratorSectionActive) {
      setIsOrchestratorMenuOpen(false);
    }
  }, [isOrchestratorSectionActive]);

  return (
    <aside className={sidebarClass}>
      {/* Hamburger / Close / Collapse — desktop only; mobile uses TopBar + close here when open */}
      {(!isMobile || open) && (
        <div className={styles['sidebar-top']}>
          <button
            className={styles['hamburger-btn']}
            onClick={isMobile ? onClose : onToggleCollapse}
            aria-label={isMobile ? 'Fechar menu' : (collapsed ? 'Expandir menu' : 'Recolher menu')}
          >
            <Icon name={isMobile ? 'close' : 'menu'} />
          </button>
        </div>
      )}

      {/* Profile */}
      <div className={`${styles['profile-section']} ${isCollapsed ? styles['profile-section-collapsed'] : ''} ${isMobile ? styles['profile-section-mobile'] : ''}`}>
        <div className={`${styles['profile-avatar']} ${isCollapsed ? styles['profile-avatar-collapsed'] : ''}`}>
          <span className={styles['profile-initials']}>
            {displayName.charAt(0).toUpperCase()}
          </span>
        </div>
        {!isCollapsed && (
          <>
            <span className={styles['profile-greeting']}>
              Olá, {displayName}!
            </span>
            <button
              type="button"
              className={styles['profile-manage']}
              onClick={handleManageAccount}
            >
              Gerenciar conta
            </button>
          </>
        )}
      </div>

      {/* Divider */}
      <div className={`${styles['sidebar-divider']} ${isMobile ? styles['sidebar-divider-mobile'] : ''}`} />

      {/* Navigation */}
      <nav className={`${styles['sidebar-nav']} ${isMobile ? styles['sidebar-nav-mobile'] : ''}`}>
        {canAccessDashboard(roles) && (
          <NavLink
            to="/"
            end
            className={() => getNavClass({ isActive: isHomeSectionActive })}
            onClick={(event) => handleNavClick(event, '/')}
            title={isCollapsed ? 'Home' : undefined}
          >
            <Icon name="home" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Home</span>}
          </NavLink>
        )}

        {canAccessStudents(roles) && (
          <NavLink to="/alunas" className={getNavClass} onClick={(event) => handleNavClick(event, '/alunas')} title={isCollapsed ? 'Alunas' : undefined}>
            <Icon name="face_4" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Alunas</span>}
          </NavLink>
        )}

        {canAccessCommercial(roles) && (
          <div
            className={[
              styles['menu-group'],
              styles['menu-group-with-submenu'],
              isCommercialMenuOpen ? styles['menu-group-open'] : '',
              isCommercialSectionActive ? styles['menu-group-active'] : '',
            ].filter(Boolean).join(' ')}
          >
            <button
              type="button"
              className={[
                styles['menu-item'],
                styles['menu-trigger'],
                isCollapsed ? styles['menu-item-collapsed'] : '',
                isMobile ? styles['menu-item-mobile'] : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setIsCommercialMenuOpen((current) => !current)}
              title={isCollapsed ? 'Comercial' : undefined}
              aria-expanded={isCommercialMenuOpen}
              aria-controls="commercial-submenu"
            >
              <Icon name="enterprise" className={styles['menu-icon']} />
              {!isCollapsed && <span className={styles['menu-text']}>Comercial</span>}
              {!isCollapsed && (
                <Icon
                  name="arrow_forward_ios"
                  className={[
                    styles['menu-arrow'],
                    isCommercialMenuOpen ? styles['menu-arrow-open'] : '',
                  ].filter(Boolean).join(' ')}
                />
              )}
            </button>

            <div id="commercial-submenu" className={styles.submenu}>
              {COMMERCIAL_LISTING_ITEMS.map((item) => {
                const itemPath = getCommercialListingPath(item.key);

                return (
                  <NavLink
                    key={item.key}
                    to={itemPath}
                    end={item.key === 'leads'}
                    className={getSubmenuNavClass}
                    onClick={(event) => handleNavClick(event, itemPath)}
                  >
                    <span className={styles['submenu-item-label']}>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {canAccessOnboarding(roles) && (
          <NavLink to="/acompanhamento-onboarding" className={getNavClass} onClick={(event) => handleNavClick(event, '/acompanhamento-onboarding')} title={isCollapsed ? 'Onboarding' : undefined}>
            <Icon name="checklist" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Onboarding</span>}
          </NavLink>
        )}

        {canAccessOrchestrator(roles) && (
          <div
            className={[
              styles['menu-group'],
              styles['menu-group-with-submenu'],
              isOrchestratorMenuOpen ? styles['menu-group-open'] : '',
              isOrchestratorSectionActive ? styles['menu-group-active'] : '',
            ].filter(Boolean).join(' ')}
          >
            <button
              type="button"
              className={[
                styles['menu-item'],
                styles['menu-trigger'],
                isCollapsed ? styles['menu-item-collapsed'] : '',
                isMobile ? styles['menu-item-mobile'] : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setIsOrchestratorMenuOpen((current) => !current)}
              title={isCollapsed ? 'Orquestrador' : undefined}
              aria-expanded={isOrchestratorMenuOpen}
              aria-controls="orchestrator-submenu"
            >
              <Icon name="arrow_split" className={styles['menu-icon']} />
              {!isCollapsed && <span className={styles['menu-text']}>Orquestrador</span>}
              {!isCollapsed && (
                <Icon
                  name="arrow_forward_ios"
                  className={[
                    styles['menu-arrow'],
                    isOrchestratorMenuOpen ? styles['menu-arrow-open'] : '',
                  ].filter(Boolean).join(' ')}
                />
              )}
            </button>

            <div id="orchestrator-submenu" className={styles.submenu}>
              {ORCHESTRATOR_TABS.map((tab) => {
                const tabPath = getOrchestratorTabPath(tab.key);

                return (
                  <NavLink
                    key={tab.key}
                    to={tabPath}
                    end={tab.key === 'treinos'}
                    className={getSubmenuNavClass}
                    onClick={(event) => handleNavClick(event, tabPath)}
                  >
                    <span className={styles['submenu-item-label']}>{tab.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {canAccessPerformance(roles) && (
          <NavLink to="/performance" className={getNavClass} onClick={(event) => handleNavClick(event, '/performance')} title={isCollapsed ? 'Performance' : undefined}>
            <Icon name="monitoring" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Performance</span>}
          </NavLink>
        )}

        {canAccessPerformanceSupportDashboard(roles) && !canAccessDashboard(roles) && (
          <NavLink to="/suporte-performance" className={getNavClass} onClick={(event) => handleNavClick(event, '/suporte-performance')} title={isCollapsed ? 'Suporte da performance' : undefined}>
            <Icon name="support_agent" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Suporte da performance</span>}
          </NavLink>
        )}

        {canAccessRecentBodyPhotosDashboard(roles) && !canAccessDashboard(roles) && (
          <NavLink to="/fotos-recentes" className={getNavClass} onClick={(event) => handleNavClick(event, '/fotos-recentes')} title={isCollapsed ? 'Fotos recentes' : undefined}>
            <Icon name="photo_library" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Fotos recentes</span>}
          </NavLink>
        )}

        {canAccessSensitiveStudentsDashboard(roles) && !canAccessDashboard(roles) && (
          <NavLink to="/alunas-sensiveis" className={getNavClass} onClick={(event) => handleNavClick(event, '/alunas-sensiveis')} title={isCollapsed ? 'Alunas sensíveis' : undefined}>
            <Icon name="favorite" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Alunas sensíveis</span>}
          </NavLink>
        )}

        {canAccessExercises(roles) && (
          <NavLink to="/exercicios" className={getNavClass} onClick={(event) => handleNavClick(event, '/exercicios')} title={isCollapsed ? 'Exercícios' : undefined}>
            <Icon name="view_list" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Exercícios</span>}
          </NavLink>
        )}

        {canAccessTrainings(roles) && (
          <NavLink to="/treinos" className={getNavClass} onClick={(event) => handleNavClick(event, '/treinos')} title={isCollapsed ? 'Treinos' : undefined}>
            <Icon name="exercise" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Treinos</span>}
          </NavLink>
        )}

        {canAccessAdministration(roles) && (
          <NavLink to="/administracao" className={getNavClass} onClick={(event) => handleNavClick(event, '/administracao')} title={isCollapsed ? 'Administração' : undefined}>
            <Icon name="admin_panel_settings" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Administração</span>}
          </NavLink>
        )}

        {canAccessFullAdministration(roles) && (
          <NavLink
            to="/gestao-entregas-treinos"
            className={getNavClass}
            onClick={(event) => handleNavClick(event, '/gestao-entregas-treinos')}
            title={isCollapsed ? 'Gestão de entregas' : undefined}
          >
            <Icon name="assignment_turned_in" className={styles['menu-icon']} />
            {!isCollapsed && <span className={styles['menu-text']}>Gestão de entregas</span>}
          </NavLink>
        )}
      </nav>

      {/* Spacer */}
      <div className={styles['sidebar-spacer']} />

      {/* Logout */}
      <div className={`${styles['sidebar-footer']} ${isMobile ? styles['sidebar-footer-mobile'] : ''}`}>
        <button className={styles['logout-btn']} onClick={handleLogout} title={isCollapsed ? 'Sair' : undefined}>
          <Icon name="logout" className={styles['logout-icon']} />
          {!isCollapsed && <span>Sair</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
